import { useState, useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, FileText, ArrowRight, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LOCATIONS = ["SALEM", "BOSTON", "METHUEN", "CAPE"] as const;
const SPECIES_OPTIONS = ["Dog", "Cat"] as const;
const DAYS_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "all", label: "All time" },
] as const;

interface AnimalResult {
  id: string;
  animalId: string;
  name: string;
  species: string;
  primaryBreed: string | null;
  photoUrl: string | null;
  location: string | null;
  href: string;
}

interface InteractionResult {
  id: string;
  animalId: string;
  animalName: string | null;
  scoreCode: string | null;
  author: string | null;
  occurredAt: string;
  locationCode: string;
}

interface ReportResult {
  id: string;
  reportDate: string;
  location: string;
  type: string;
  reportKind: string;
  needsReview: boolean;
  href: string;
}

interface SearchResponse {
  query: string;
  animals: AnimalResult[];
  interactions: InteractionResult[];
  reports: ReportResult[];
}

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
}

function buildSearchUrl(q: string, location: string, species: string, days: string): string {
  const params = new URLSearchParams();
  params.set("q", q);
  params.set("limit", "5");
  if (location !== "all") params.set("location", location);
  if (species !== "all") params.set("species", species);
  if (days !== "60") params.set("days", days);
  return `/api/search?${params.toString()}`;
}

function buildInteractionHref(
  interaction: InteractionResult,
  locationFilter: string,
  daysFilter: string,
): string {
  const params = new URLSearchParams();
  params.set("animalId", interaction.animalId);

  if (locationFilter !== "all") {
    params.set("locationCode", locationFilter);
  }

  if (daysFilter !== "all") {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - parseInt(daysFilter, 10));
    const fmt = (d: Date) => d.toISOString().split("T")[0];
    params.set("startDate", fmt(startDate));
    params.set("endDate", fmt(today));
  } else {
    params.set("startDate", "2020-01-01");
  }

  if (interaction.scoreCode) {
    params.set("scoreCode", interaction.scoreCode);
  }

  return `/interactions?${params.toString()}`;
}

function buildReportsViewAllHref(locationFilter: string): string {
  const params = new URLSearchParams();
  if (locationFilter !== "all") {
    params.set("location", locationFilter);
  }
  const qs = params.toString();
  return qs ? `/reports?${qs}` : "/reports";
}

function getRecencyLabel(days: string): string {
  const opt = DAYS_OPTIONS.find((o) => o.value === days);
  return opt ? opt.label.toLowerCase() : "60 days";
}

export function GlobalSearchDialog({ open, onOpenChange, initialQuery }: GlobalSearchDialogProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [speciesFilter, setSpeciesFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("60");
  const [, navigate] = useLocation();
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const cleanupRef = useRef<(() => void) | null>(null);

  const inputWrapperRef = useCallback((node: HTMLDivElement | null) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!node) return;
    const input = node.querySelector("input");
    if (!input) return;
    const handler = () => {
      setQuery(input.value);
    };
    input.addEventListener("input", handler);
    cleanupRef.current = () => input.removeEventListener("input", handler);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setLocationFilter("all");
      setSpeciesFilter("all");
      setDaysFilter("60");
    } else if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [open, initialQuery]);

  const searchUrl = buildSearchUrl(debouncedQuery, locationFilter, speciesFilter, daysFilter);

  const { data, isLoading } = useQuery<SearchResponse>({
    queryKey: [searchUrl],
    enabled: open && debouncedQuery.length >= 2,
  });

  const handleSelect = useCallback(
    (href: string) => {
      onOpenChange(false);
      navigate(href);
    },
    [navigate, onOpenChange]
  );

  const hasResults =
    data &&
    (data.animals.length > 0 ||
      data.interactions.length > 0 ||
      data.reports.length > 0);

  const showEmpty = false;
  const hasActiveFilters = locationFilter !== "all" || speciesFilter !== "all" || daysFilter !== "60";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <div ref={inputWrapperRef}>
            <CommandInput
              placeholder="Search animals, interactions, reports..."
              value={query}
              onValueChange={setQuery}
              data-testid="input-global-search"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap" data-testid="search-filter-row">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-auto min-w-[100px]" data-testid="select-search-location">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
              <SelectTrigger className="w-auto min-w-[80px]" data-testid="select-search-species">
                <SelectValue placeholder="Species" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Species</SelectItem>
                {SPECIES_OPTIONS.map((sp) => (
                  <SelectItem key={sp} value={sp}>{sp}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={daysFilter} onValueChange={setDaysFilter}>
              <SelectTrigger className="w-auto min-w-[80px]" data-testid="select-search-days">
                <SelectValue placeholder="Recency" />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <CommandList className={query.length >= 2 ? "h-[400px]" : ""}>
        {isLoading && debouncedQuery.length >= 2 && (
          <div className="p-4 space-y-3" data-testid="search-loading">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
          </div>
        )}

        {showEmpty && (
          <CommandEmpty data-testid="search-empty">
            No results found for &ldquo;{debouncedQuery}&rdquo;{hasActiveFilters && " with current filters"}
          </CommandEmpty>
        )}

        {debouncedQuery.length >= 2 && data && (
          <CommandGroup heading="Current Animals">
            {data.animals.map((animal) => (
              <CommandItem
                key={animal.id}
                value={`animal-${animal.animalId}-${animal.name}`}
                onSelect={() => handleSelect(animal.href)}
                className="cursor-pointer"
                data-testid={`search-result-animal-${animal.animalId}`}
              >
                <AnimalAvatar
                  animalId={animal.animalId}
                  photoUrl={animal.photoUrl}
                  name={animal.name}
                  species={animal.species}
                  className="mr-2 h-7 w-7 shrink-0"
                />
                <div className="flex flex-1 items-center gap-2 min-w-0 flex-wrap">
                  <span className="font-medium truncate">{animal.name}</span>
                  {followedIds.includes(animal.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" data-testid={`heart-followed-${animal.animalId}`} />}
                  <span className="text-xs text-muted-foreground">{animal.animalId}</span>
                  {animal.species && (
                    <Badge variant="secondary" className="text-xs">{animal.species}</Badge>
                  )}
                  {animal.location && (
                    <span className="text-xs text-muted-foreground">{animal.location}</span>
                  )}
                </div>
              </CommandItem>
            ))}
            <CommandItem
              value="search-outcomed-animals"
              onSelect={() => handleSelect(`/animals?tab=history&outcomeFilter=outcomed&search=${encodeURIComponent(debouncedQuery)}`)}
              className="cursor-pointer"
              data-testid="link-search-outcomed-animals"
            >
              <ArrowRight className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Search outcomed animals for &ldquo;{debouncedQuery}&rdquo;
              </span>
            </CommandItem>
          </CommandGroup>
        )}

        {debouncedQuery.length >= 2 && (
          <CommandGroup heading="Interactions">
            {(data?.interactions ?? []).map((interaction) => (
              <CommandItem
                key={interaction.id}
                value={`interaction-${interaction.id}-${interaction.animalId}`}
                onSelect={() => handleSelect(buildInteractionHref(interaction, locationFilter, daysFilter))}
                className="cursor-pointer"
                data-testid={`search-result-interaction-${interaction.id}`}
              >
                <MessageSquare className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex flex-1 items-center gap-2 min-w-0 flex-wrap">
                  <span className="font-medium truncate">
                    {interaction.animalName ?? interaction.animalId}
                  </span>
                  {followedIds.includes(interaction.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" data-testid={`heart-followed-interaction-${interaction.id}`} />}
                  {interaction.scoreCode && (
                    <Badge variant="outline" className="text-xs">{interaction.scoreCode}</Badge>
                  )}
                  {interaction.author && (
                    <span className="text-xs text-muted-foreground">by {interaction.author}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(interaction.occurredAt).toLocaleDateString()}
                  </span>
                </div>
              </CommandItem>
            ))}
            <CommandItem
              value="search-interaction-notes"
              onSelect={() => handleSelect(`/interactions?search=${encodeURIComponent(debouncedQuery)}&startDate=2020-01-01&endDate=${format(new Date(), "yyyy-MM-dd")}&resetFilters=1`)}
              className="cursor-pointer"
              data-testid="link-search-interaction-notes"
            >
              <ArrowRight className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Search interaction notes for &ldquo;{debouncedQuery}&rdquo;
              </span>
              {(() => {
                const rawTokens = debouncedQuery.trim().split(/\s+/);
                const tokens = rawTokens.filter(t => t.length >= 2);
                const phraseMode = tokens.length <= 1 && tokens.length < rawTokens.length;
                if (phraseMode) return (
                  <span className="text-xs text-muted-foreground/70 ml-6">
                    Matched as phrase
                  </span>
                );
                if (tokens.length > 1) return (
                  <span className="text-xs text-muted-foreground/70 ml-6">
                    Each word matched separately
                  </span>
                );
                return null;
              })()}
            </CommandItem>
          </CommandGroup>
        )}

        {data && data.reports.length > 0 && (
          <CommandGroup heading="Reports">
            {data.reports.map((report) => (
              <CommandItem
                key={report.id}
                value={`report-${report.id}-${report.reportDate}`}
                onSelect={() => handleSelect(report.href)}
                className="cursor-pointer"
                data-testid={`search-result-report-${report.id}`}
              >
                <FileText className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex flex-1 items-center gap-2 min-w-0 flex-wrap">
                  <span className="font-medium">{report.reportDate}</span>
                  <span className="text-xs text-muted-foreground">{report.location}</span>
                  <Badge variant="secondary" className="text-xs">{report.reportKind}</Badge>
                  {report.needsReview && (
                    <Badge variant="outline" className="text-xs text-warning border-warning/30 bg-warning/5" data-testid={`badge-needs-review-${report.id}`}>Needs Review</Badge>
                  )}
                </div>
              </CommandItem>
            ))}
            {(() => {
              const effectiveLocation = locationFilter !== "all"
                ? locationFilter
                : data.reports.length > 0 && data.reports.every((r) => r.location === data.reports[0].location)
                  ? data.reports[0].location
                  : null;
              return (
                <CommandItem
                  value="view-all-reports"
                  onSelect={() => handleSelect(buildReportsViewAllHref(effectiveLocation ?? "all"))}
                  className="cursor-pointer"
                  data-testid="link-view-all-reports"
                >
                  <ArrowRight className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    View all reports{effectiveLocation ? ` in ${effectiveLocation}` : ""}
                  </span>
                </CommandItem>
              );
            })()}
          </CommandGroup>
        )}
        {hasResults && (
          <div className="px-4 py-2 border-t text-xs text-muted-foreground" data-testid="text-search-window-note">
            {daysFilter === "all"
              ? "Showing results across all time"
              : `Interactions and reports limited to the last ${getRecencyLabel(daysFilter)}`}
          </div>
        )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
